const API_PATH = '/api/studio-reviews';
const TRUST_API_PATH = '/api/studio-trust-reviews';

export class ReviewApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'ReviewApiError';
    this.status = status;
  }
}

async function request(method, body, apiPath = API_PATH, query = '') {
  const response = await fetch(`${apiPath}${query}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    credentials: 'same-origin',
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new ReviewApiError(payload?.error || `Cloud review service returned ${response.status}`, response.status);
  return payload;
}

export const loadCloudReviews = () => request('GET');

export const loadCloudTrustReviews = (trustId) => {
  if (!trustId) throw new ReviewApiError('A Trust is required for Trust-scoped review');
  return request('GET', undefined, TRUST_API_PATH, `?trustId=${encodeURIComponent(trustId)}`);
};

export function saveCloudReview(roleKey, review, librarySchemaVersion) {
  const isTrust = review.reviewScope === 'trust';
  return request('PUT', {
    roleKey,
    status: review.status || 'not_reviewed',
    comments: review.comments || '',
    reviewer: review.reviewer || undefined,
    updatedAt: review.updatedAt || undefined,
    librarySchemaVersion,
    contentVersion: review.contentVersion || undefined,
    reviewScope: review.reviewScope || 'national',
    trustId: review.trustId || undefined,
    assignmentId: review.assignmentId || undefined,
    recordKey: review.recordKey || undefined,
  }, isTrust ? TRUST_API_PATH : API_PATH);
}

export function importCloudReviews(reviews, librarySchemaVersion) {
  const payload = Object.fromEntries(Object.entries(reviews || {}).map(([roleKey, review]) => [roleKey, {
    status: review.status || 'not_reviewed',
    comments: review.comments || '',
    reviewer: review.reviewer || undefined,
    updatedAt: review.updatedAt || undefined,
    librarySchemaVersion,
    contentVersion: review.contentVersion || undefined,
    reviewScope: review.reviewScope || 'national',
    trustId: review.trustId || undefined,
    assignmentId: review.assignmentId || undefined,
    recordKey: review.recordKey || undefined,
  }]));
  return request('POST', { reviews: payload });
}

export const runClinicalReviewWorkflow = (action) => request('POST', { action });

export function runTrustReviewWorkflow(trustId, action, values = {}) {
  if (!trustId) throw new ReviewApiError('A Trust is required for Trust-scoped review');
  return request('POST', { trustId, action, ...values }, TRUST_API_PATH);
}

export function runPlatformClinicalReviewWorkflow(reviewerUserId, action, comment = '') {
  return request('POST', { reviewerUserId, action, comment });
}
